import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import Form from "src/components/base/form";
import { ErrorMessage, Field, FieldGroup, Fieldset, RequiredLabel } from "src/components/base/fieldset";
import { Button, PrimaryButton } from "src/components/base/button";
import { useForm } from "@tanstack/react-form";
import { SimpleDeviceGroup } from "src/api/generated/frontend_internal_v1";
import { ApiInternal } from "src/api/api_internal";
import { Textarea } from "src/components/base/textarea";

/**
 * The properties for {@link ChangeDeviceGroupDescDialog}
 */
export type ChangeDeviceGroupDescDialogProps = {
    /** Whether the dialog is open or not */
    isOpen: boolean;
    /** Callback that should be called when closing the popup */
    onClose: () => void;
    /** Callback that should be called when the form has been submitted successfully */
    onSubmit: () => void;
    /** The device group to rename */
    deviceGroup: SimpleDeviceGroup | undefined;
};

/**
 * A dialog to rename a device
 */
export default function ChangeDeviceGroupDescDialog(props: ChangeDeviceGroupDescDialogProps) {
    const [t] = useTranslation("dialog-change-device-group-desc");
    const [tg] = useTranslation();

    const form = useForm({
        defaultValues: { description: props.deviceGroup?.description ?? "" },
        onSubmit: async ({ value }) => {
            if (!props.deviceGroup) return;
            await ApiInternal.deviceGroups.setDescription(props.deviceGroup.uuid, value.description);
            props.onSubmit();
        },
    });

    useEffect(() => {
        form.reset();
    }, [props.isOpen, props.deviceGroup]);

    if (!props.deviceGroup) return null;

    return (
        <Dialog open={props.isOpen} onClose={() => props.onClose()}>
            <DialogTitle>{t("heading.change-device-group-desc", { deviceGroup: props.deviceGroup.name })}</DialogTitle>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"description"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.new-desc")}</RequiredLabel>
                                        <Textarea
                                            value={fieldApi.state.value}
                                            maxLength={1024}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            resizable={false}
                                        />
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>
                            <DialogActions>
                                <Button plain={true} onClick={props.onClose}>
                                    {tg("button.cancel")}
                                </Button>
                                <PrimaryButton type={"submit"}>{t("button.change-device-group-desc")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
